import type { BiteFeedbackEvent, CompletionReason } from "@/core/types";
import type { ManeuverType } from "@/core/scoring/types";
import type { Platform } from "@/platform/Platform";
import { CURRENT_SCORING_VERSION, type DeathReason, type RunSource, type RunStatus, type RunSubmission, type Stars } from "./types";

export interface RunTrackerContext {
  levelId: string;
  worldId: string;
  runSource: RunSource;
  /** PR34: this run's frozen Mobile/Desktop category - see `RunSubmission.platform`. `GameScene` always sets it; omitted only by older unit-test fixtures. */
  platform?: Platform;
}

export interface RunOutcome {
  status: RunStatus;
  /**
   * Final Score's `score` field, kept as the single required field for
   * backward compatibility with every pre-Scoring-V2 call site/test - it
   * IS Bite Score for those. Real Scoring V2 runs pass `biteScore`/
   * `skillScore`/`paceBonus` explicitly instead; when they're omitted,
   * `biteScore` defaults to this `score` value and `skillScore`/
   * `paceBonus` default to 0, so an old-style call produces exactly the
   * same `scoringVersion: 2` row a `scoringVersion: 1` one always meant
   * (bite score only, no skill/pace).
   */
  score: number;
  biteScore?: number;
  skillScore?: number;
  /** Always 0 for a failed run or a `starfruit_gate` completion - the caller's responsibility, not re-derived here. */
  paceBonus?: number;
  moveCount?: number;
  maneuverCounts?: Record<ManeuverType, number>;
  doubleDangerCount?: number;
  bestFlowStreak?: number;
  /** TRICK BITE / CLUTCH BITE counts - see `RunSubmission`'s own doc. Default to 0, same precedent as every other Scoring V2 counter here. */
  trickBiteCount?: number;
  clutchBiteCount?: number;
  stars: Stars;
  finalLength: number;
  deathReason: DeathReason | null;
  /**
   * Why the gate was open for a completed run - straight from
   * `LevelGame`'s snapshot (`completionReason`), never re-derived here.
   * Optional only so every pre-existing call site (none of which ever
   * dealt with STARFRUIT) keeps compiling unchanged - omitting it on a
   * completed run defaults to `"length_target"` (correct for all of
   * them), and a failed run's value is always `null` regardless of what's
   * passed in.
   */
  completionReason?: CompletionReason | null;
}

/**
 * Framework-agnostic collector for one level attempt's run stats. Fed by
 * two independent inputs, neither of which is Phaser:
 *  - `recordBiteEvent()`, forwarded from the engine's own
 *    `BiteFeedbackEvent` (see `LevelGame`/`GameScene`) - the single
 *    source of truth for what actually happened, never re-derived here;
 *  - `addActiveMs()`, driven by the scene's own frame delta, called only
 *    while the level is actually being actively played (not paused, not
 *    HOW TO PLAY, not the first-run tutorial, not a Game Over/Level
 *    Complete overlay, not the automatic gate-exit walk).
 *
 * `buildSubmission()` turns the accumulated counters into a ready-to-send
 * `RunSubmission`, generating a fresh `run_id` - the idempotency key the
 * server relies on to never record the same run twice, including across
 * offline-queue retries of that same submission object. `buildSubmission()`
 * itself is idempotent: the first call finalizes and caches the result: a
 * duplicate/re-entrant completion callback calling it again gets back the
 * exact same submission (same run_id included), never a second one - "a
 * completed run is finalized exactly once" holds by construction, not by
 * relying on every caller to guard against being invoked twice.
 */
export class RunTracker {
  /**
   * Generated once, at construction - the single id every submission and
   * analytics event for this run shares (see `src/analytics`), never
   * regenerated by `buildSubmission()` itself. A fresh `RunTracker` (a new
   * attempt, including REPLAY/RETRY, which each construct a brand new
   * `GameScene`/`RunTracker`) always gets its own id - it never persists
   * across runs and is never usable to identify a person across sessions.
   */
  readonly runId: string = crypto.randomUUID();
  private cachedSubmission: RunSubmission | null = null;
  private activeMs = 0;
  private pausedMs = 0;
  private pauseCount = 0;
  private safeCount = 0;
  private rushCount = 0;
  private chaosCount = 0;
  private maxRushStreak = 0;
  private currentRushStreak = 0;
  private jackpotCount = 0;
  private wormholeCount = 0;
  private speedTrialStartedCount = 0;
  private speedTrialSurvivedCount = 0;
  private scoreHitCount = 0;
  private shrinkCount = 0;
  private starfruitSpawnedCount = 0;
  private starfruitEatenCount = 0;

  constructor(private readonly context: RunTrackerContext) {}

  /** Call every frame the level is actively controllable - never while paused/help/intro/exiting/game-over/level-complete. Negative/zero deltas are ignored, never subtracted. */
  addActiveMs(deltaMs: number): void {
    if (deltaMs > 0) this.activeMs += deltaMs;
  }

  /** Call every frame the level is Paused (including the HOW TO PLAY overlay, which pauses the same way) - kept entirely separate from `activeDurationMs`/Pace Bonus, for run telemetry only. */
  addPausedMs(deltaMs: number): void {
    if (deltaMs > 0) this.pausedMs += deltaMs;
  }

  /** Call exactly once per pause *start* (false -> true transition), never on resume - counts how many times this run was paused, for telemetry. */
  recordPauseStart(): void {
    this.pauseCount += 1;
  }

  /** Reads the accumulated active duration so far, without finalizing/caching a submission - needed to compute Pace Bonus (which itself becomes part of `buildSubmission()`'s `outcome`) before the run is actually built. Safe to call any number of times. */
  getActiveDurationMs(): number {
    return Math.round(this.activeMs);
  }

  /** Wall-clock time since this run started - active control time plus Paused/HOW TO PLAY time (`activeMs + pausedMs`), unlike `getActiveDurationMs()`. Analytics-only (see `src/analytics`'s `elapsed_ms` properties); never read by scoring. */
  getElapsedMs(): number {
    return Math.round(this.activeMs + this.pausedMs);
  }

  /** Snapshot of this run's bite-kind counters and max RUSH streak so far - analytics-only (see `src/analytics`'s `run_finished`), reading the same counters `buildSubmission()` eventually reports, without finalizing/caching a submission. Safe to call any number of times, including before the run ends. */
  getBiteCounts(): { safe: number; rush: number; chaos: number; maxRushStreak: number } {
    return { safe: this.safeCount, rush: this.rushCount, chaos: this.chaosCount, maxRushStreak: this.maxRushStreak };
  }

  /**
   * Endless-only: the private CHAOS-outcome and pause counters
   * `buildSubmission()` already reads internally, but `getBiteCounts()`
   * doesn't expose (that method only ever needed safe/rush/chaos/
   * maxRushStreak for Campaign/Daily/Endless analytics). Exposed directly
   * so `buildEndlessSubmissionMetrics()` (`src/endless/submissionMetrics.ts`)
   * never has to go through Campaign's level/world/stars-shaped
   * `buildSubmission()`/`RunOutcome` path with fake values forced into it -
   * see that module's own doc. Safe to call any number of times, including
   * before the run ends; never caches (Endless's own `finishEndlessRun()`
   * guard already ensures a run is finalized at most once).
   */
  getEndlessAggregateCounters(): {
    jackpotCount: number;
    wormholeCount: number;
    shrinkCount: number;
    speedTrialStartedCount: number;
    speedTrialSurvivedCount: number;
    scoreHitCount: number;
    pauseCount: number;
    pausedDurationMs: number;
  } {
    return {
      jackpotCount: this.jackpotCount,
      wormholeCount: this.wormholeCount,
      shrinkCount: this.shrinkCount,
      speedTrialStartedCount: this.speedTrialStartedCount,
      speedTrialSurvivedCount: this.speedTrialSurvivedCount,
      scoreHitCount: this.scoreHitCount,
      pauseCount: this.pauseCount,
      pausedDurationMs: Math.round(this.pausedMs),
    };
  }

  /** Mirrors `LevelGame`'s own RUSH-streak reset rule: SAFE and CHAOS both reset it, only RUSH extends it - see `LevelGame.resetRush()`. */
  recordBiteEvent(event: BiteFeedbackEvent): void {
    switch (event.kind) {
      case "safe":
        this.safeCount += 1;
        this.currentRushStreak = 0;
        break;
      case "rush":
        this.rushCount += 1;
        this.currentRushStreak += 1;
        this.maxRushStreak = Math.max(this.maxRushStreak, this.currentRushStreak);
        break;
      case "chaos":
        this.chaosCount += 1;
        this.currentRushStreak = 0;
        this.recordChaosOutcome(event.outcome);
        break;
      case "speed-trial-success":
        this.speedTrialSurvivedCount += 1;
        break;
      case "starfruit":
        this.starfruitEatenCount = 1;
        this.currentRushStreak = 0;
        break;
    }
  }

  /** Called once by the scene the first time STARFRUIT is observed on the board (whether or not it's ever eaten) - separate from `recordBiteEvent` since spawning, unlike every other counter here, isn't itself an eaten-bite event. STARFRUIT can spawn at most once per run, so this is idempotent-safe to call more than once. */
  recordStarfruitSpawned(): void {
    this.starfruitSpawnedCount = 1;
  }

  private recordChaosOutcome(outcome: "jackpot" | "wormhole" | "speed-trial" | "score-hit" | "shrink"): void {
    switch (outcome) {
      case "jackpot":
        this.jackpotCount += 1;
        break;
      case "wormhole":
        this.wormholeCount += 1;
        break;
      case "speed-trial":
        this.speedTrialStartedCount += 1;
        break;
      case "score-hit":
        this.scoreHitCount += 1;
        break;
      case "shrink":
        this.shrinkCount += 1;
        break;
    }
  }

  buildSubmission(outcome: RunOutcome): RunSubmission {
    if (this.cachedSubmission) return this.cachedSubmission;
    const maneuverCounts = outcome.maneuverCounts;
    this.cachedSubmission = {
      runId: this.runId,
      levelId: this.context.levelId,
      worldId: this.context.worldId,
      status: outcome.status,
      score: outcome.score,
      stars: outcome.stars,
      finalLength: outcome.finalLength,
      activeDurationMs: Math.round(this.activeMs),
      safeCount: this.safeCount,
      rushCount: this.rushCount,
      chaosCount: this.chaosCount,
      maxRushStreak: this.maxRushStreak,
      jackpotCount: this.jackpotCount,
      speedTrialStartedCount: this.speedTrialStartedCount,
      speedTrialSurvivedCount: this.speedTrialSurvivedCount,
      scoreHitCount: this.scoreHitCount,
      shrinkCount: this.shrinkCount,
      wormholeCount: this.wormholeCount,
      starfruitSpawnedCount: this.starfruitSpawnedCount,
      starfruitEatenCount: this.starfruitEatenCount,
      completionReason:
        outcome.status === "completed" ? (outcome.completionReason ?? "length_target") : null,
      deathReason: outcome.deathReason,
      runSource: this.context.runSource,
      ...(this.context.platform ? { platform: this.context.platform } : {}),
      scoringVersion: CURRENT_SCORING_VERSION,
      biteScore: outcome.biteScore ?? outcome.score,
      skillScore: outcome.skillScore ?? 0,
      paceBonus: outcome.paceBonus ?? 0,
      moveCount: outcome.moveCount ?? 0,
      perfectFoldCount: maneuverCounts?.perfect_fold ?? 0,
      lastSecondTurnCount: maneuverCounts?.last_second_turn ?? 0,
      tailGrazeCount: maneuverCounts?.tail_graze ?? 0,
      threadTheNeedleCount: maneuverCounts?.thread_the_needle ?? 0,
      doubleDangerCount: outcome.doubleDangerCount ?? 0,
      bestFlowStreak: outcome.bestFlowStreak ?? 0,
      pauseCount: this.pauseCount,
      pausedDurationMs: Math.round(this.pausedMs),
      trickBiteCount: outcome.trickBiteCount ?? 0,
      clutchBiteCount: outcome.clutchBiteCount ?? 0,
    };
    return this.cachedSubmission;
  }
}
